import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["column", "appMockup"];

  connect() {
    // Store initial scroll position when controller connects
    this.initialScrollY = window.scrollY;
    this.hasInitialized = false;

    this.setupScrollListener();
    this.setupIntersectionObserver();
  }

  disconnect() {
    if (this.scrollListener) {
      window.removeEventListener("scroll", this.scrollListener);
    }
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  setupScrollListener() {
    this.scrollListener = this.handleScroll.bind(this);
    window.addEventListener("scroll", this.scrollListener, { passive: true });

    // Don't call handleScroll immediately - wait for actual scroll
  }

  setupIntersectionObserver() {
    // Observer for app mockup fade-in animation
    if (this.hasAppMockupTarget) {
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              // Add animation classes when mockup comes into view
              entry.target.classList.add("opacity-100", "scale-100");
              entry.target.classList.remove("opacity-0", "scale-95");
            }
          });
        },
        {
          threshold: 0.2, // Trigger when 20% visible
          rootMargin: "0px 0px -100px 0px", // Start animation slightly before fully in view
        }
      );

      this.observer.observe(this.appMockupTarget);
    }
  }

  handleScroll() {
    if (!this.hasColumnTarget) return;

    const scrollY = window.scrollY;
    const windowHeight = window.innerHeight;

    // Initialize column data on first scroll
    if (!this.hasInitialized) {
      this.hasInitialized = true;
      this.columnData = [];

      this.columnTargets.forEach((column, index) => {
        const rect = column.getBoundingClientRect();
        const columnTop = rect.top + scrollY;
        const columnCenter = columnTop + rect.height / 2;

        // Calculate initial scroll progress
        const initialProgress =
          (this.initialScrollY + windowHeight / 2 - columnCenter) /
          windowHeight;

        this.columnData.push({
          initialProgress: initialProgress,
          element: column,
        });
      });
    }

    this.columnTargets.forEach((column, index) => {
      const rect = column.getBoundingClientRect();
      const columnTop = rect.top + scrollY;
      const columnCenter = columnTop + rect.height / 2;

      // Calculate current scroll progress
      const currentProgress =
        (scrollY + windowHeight / 2 - columnCenter) / windowHeight;

      // Calculate relative progress from initial position
      const relativeProgress =
        currentProgress - (this.columnData[index]?.initialProgress || 0);

      // Alternate direction for each column (odd columns go up, even go down)
      const direction = index % 2 === 0 ? -1 : 1;

      // Calculate translation amount based on relative progress
      const translateY = relativeProgress * direction * 15; // 15px max movement

      // Apply transform
      column.style.transform = `translateY(${translateY}px)`;
    });
  }
}
